import React, { useState, useRef, useMemo, useEffect } from 'react';
import './PersonaSurveyPage.scss';
import { MultiSelectOption, OnboardingWrapper } from '../../../components';
import { useOnboarding, generalSnackBarAtom } from '../../../data-access';
import { JOB_OPTIONS } from '../constants';
import type { JobOptions } from '../constants';
import { ReactComponent as SearchIcon } from '../../../assets/onboarding/search.svg';
import { TextField } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useRecoilState } from 'recoil';
import { useSearchParams } from 'react-router-dom';

export const PersonaSurveyPage: React.FC = () => {
  const { currentOnboardingStep, goToNextStep, goToPreviousStep, updateSurveyData } =
    useOnboarding();
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [_, setGeneralSnackbar] = useRecoilState(generalSnackBarAtom);
  const filterOptions = useMemo(
    () =>
      createFilterOptions<JobOptions>({
        matchFrom: 'any',
        stringify: (option) => option.label,
      }),
    []
  );

  useEffect(() => {
    const subscriptionSuccess = searchParams.get('subscriptionSuccess');
    if (subscriptionSuccess === 'true') {
      setGeneralSnackbar('Payment successful! Welcome to Lifestack 🎉');
      // Remove the query parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('subscriptionSuccess');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePersonaSelect = (persona: string) => {
    setSelectedPersona(persona);
  };

  const handleContinue = () => {
    if (selectedPersona) {
      updateSurveyData({ job: selectedPersona });
      goToNextStep();
    }
  };

  const handleSkip = () => {
    goToNextStep();
  };
  const sortedJobOptions = useMemo(() => {
    return JOB_OPTIONS.sort((a, b) => a.label.localeCompare(b.label));
  }, []);
  return (
    <OnboardingWrapper
      currentOnboardingStep={currentOnboardingStep}
      onBack={goToPreviousStep}
      title={'To help Lifestack get to know you, which of the following best describes you?'}
      buttons={[
        {
          text: 'Continue',
          onPress: handleContinue,
          variant: 'contained',
          disabled: !selectedPersona,
        },
        {
          text: 'Skip',
          onPress: handleSkip,
          variant: 'text',
        },
      ]}
      buttonContainerStyle={{ flexDirection: 'column' }}
    >
      <div className="persona-survey-page" ref={dropdownRef}>
        <Autocomplete
          disablePortal
          options={sortedJobOptions}
          filterOptions={filterOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
          renderInput={(params) => (
            <div className="persona-survey-page__search">
              <SearchIcon color="inherit" className="persona-survey-page__search__icon" />
              <TextField
                {...params}
                placeholder="Search..."
                variant="standard"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  className: 'persona-survey-page__input',
                }}
              />
            </div>
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <MultiSelectOption
                key={option.id}
                value={option.id}
                option={option.label}
                selectedValues={[selectedPersona]}
                handleSelect={handlePersonaSelect}
                className="persona-survey-page__option"
                textClassName="persona-survey-page__option__text"
              />
            </li>
          )}
          onChange={(event, value) => {
            setSelectedPersona(value?.id || null);
          }}
          open
          classes={{
            root: 'persona-survey-page__autocomplete',
            inputRoot: 'persona-survey-page__search',
            listbox: 'persona-survey-page__dropdown',
            option: 'persona-survey-page__option',
            paper: 'persona-survey-page__dropdown',
          }}
        />
      </div>
    </OnboardingWrapper>
  );
};
